import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

// --- IMPORTACIÓN DE IMÁGENES ---
import imgPanel from "../static/media/sistema/Panel.png";
import imgSecciones from "../static/media/sistema/Secciones_categorias_marcas.png";
import imgProductos from "../static/media/sistema/Productos.png";
import imgRegistros from "../static/media/sistema/Registros.png";
import imgEstadisticas from "../static/media/sistema/Estadisticas.png";
import imgVariantes from "../static/media/sistema/Variantes.png";
import imgFacturas from "../static/media/sistema/Facturas.png";
import imgProveedor from "../static/media/sistema/Proveedor.png";

const MotionBox = motion(Box);

const features = [
  {
    id: "panel",
    title: "Panel de Control",
    desc: "Un dashboard centralizado con accesos rápidos a todos los módulos. Supervisa tu operación diaria, desde facturación hasta ecommerce, en una sola pantalla clara y sin distracciones.",
    image: imgPanel,
  },
  {
    id: "catalogo",
    title: "Catálogo y Marcas",
    desc: "Organiza tu inventario visualmente por secciones, categorías y marcas. Genera códigos únicos o de barras para diferenciar productos fácilmente y mantén un catálogo digital estructurado.",
    image: imgSecciones,
  },
  {
    id: "productos",
    title: "Inventario en Tiempo Real",
    desc: "Visualiza rápidamente el estado de tus productos. Identifica precios de venta, costos con IVA, disponibilidad en bodegas y alertas visuales cuando el stock llega al mínimo permitido.",
    image: imgProductos,
  },
  {
    id: "variantes",
    title: "Variantes y Bodegas",
    desc: "Asigna mercancía a múltiples bodegas y crea variaciones (colores, tallas) o combos comerciales. Todo se descuenta del inventario base de forma automática sin crear duplicados.",
    image: imgVariantes,
  },
  {
    id: "compras",
    title: "Compras por XML",
    desc: "Olvida el ingreso manual. Simplemente carga el archivo XML entregado por tu proveedor y el sistema registrará la compra y actualizará el stock de forma automática.",
    image: imgProveedor,
  },
  {
    id: "facturacion",
    title: "Facturación Electrónica",
    desc: "Emite facturas y comprobantes al instante. Asigna vendedores, registra el método de pago, calcula fechas de vencimiento y envía los documentos aprobados por el SRI al cliente.",
    image: imgFacturas,
  },
  {
    id: "trazabilidad",
    title: "Registros de Movimientos",
    desc: "Conoce la historia exacta de cada producto: cuántas unidades se compraron, cuántas se vendieron, clientes, proveedores vinculados y el estado de cuentas por cobrar.",
    image: imgRegistros,
  },
  {
    id: "estadisticas",
    title: "Rentabilidad y Gráficas",
    desc: "Toma decisiones inteligentes. Gráficas comparativas te muestran tus costos de compra vs. precio de venta, el margen de utilidad neta y te indican qué proveedor te ofreció el mejor precio.",
    image: imgEstadisticas,
  },
];

export default function SystemShowcase() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);

  // Auto-rotación de pestañas (auto-play)
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          setActiveIndex((prev) => (prev + 1) % features.length);
          return 0;
        }
        return oldProgress + 1;
      });
    }, 60);

    return () => clearInterval(timer);
  }, [activeIndex]);

  // Auto-scroll solo para la versión móvil
  useEffect(() => {
    if (isMobile && scrollRef.current) {
      const activeElement = scrollRef.current.children[activeIndex];
      if (activeElement) {
        scrollRef.current.scrollTo({
          left: activeElement.offsetLeft - 24,
          behavior: "smooth",
        });
      }
    }
  }, [activeIndex, isMobile]);

  const handleSelect = (index) => {
    setActiveIndex(index);
    setProgress(0);
  };

  return (
    <Box
      component="section"
      id="funcionamiento"
      sx={{
        py: { xs: 6, md: 8 },
        // Fondo azul corporativo solicitado
        bgcolor: "#1A2B4A",
        position: "relative",
        overflow: "hidden",
        minHeight: { md: "90vh" },
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Glow de fondo para darle profundidad al azul */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "-10%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Encabezado adaptado para fondo oscuro */}
        <MotionBox
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{ mb: { xs: 4, md: 5 }, textAlign: { xs: "left", md: "center" } }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 750,
              fontSize: { xs: "1.8rem", md: "2.6rem" },
              color: "#FFFFFF",
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Todo el control de tu negocio{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #4F8CFF 0%, #0EA5E9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              en un solo lugar
            </Box>
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: { xs: "0.95rem", md: "1rem" },
              lineHeight: 1.5,
              maxWidth: 600,
              mx: { md: "auto" },
            }}
          >
            Explora las herramientas diseñadas para simplificar tus procesosd.
          </Typography>
        </MotionBox>

        {isMobile ? (
          /* ==============================================================
                              LAYOUT MÓVIL (CORREGIDO)
             ============================================================== */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                width: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                position: "relative",
                paddingTop: "56.25%", // Aspect Ratio 16:9
                bgcolor: "#0B1526", // Fondo oscuro para los bordes de la imagen
              }}
            >
              <AnimatePresence mode="wait">
                <MotionBox
                  key={activeIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {/* FIX: width 100% y objectFit contain evitan el recorte */}
                  <img
                    src={features[activeIndex].image}
                    alt={features[activeIndex].title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      objectPosition: "center",
                    }}
                  />
                </MotionBox>
              </AnimatePresence>
            </Box>

            {/* Pestañas Horizontales Móvil */}
            <Box
              ref={scrollRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 1.5,
                pb: 1,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {features.map((feature, idx) => {
                const isActive = activeIndex === idx;
                return (
                  <Box
                    key={feature.id}
                    onClick={() => handleSelect(idx)}
                    sx={{
                      position: "relative",
                      whiteSpace: "nowrap",
                      px: 2,
                      py: 1,
                      borderRadius: "20px",
                      bgcolor: isActive
                        ? "rgba(79,140,255,0.15)"
                        : "rgba(255,255,255,0.05)",
                      color: isActive ? "#4F8CFF" : "rgba(255,255,255,0.5)",
                      border: "1px solid",
                      borderColor: isActive ? "#4F8CFF" : "transparent",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      overflow: "hidden",
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          height: "3px",
                          width: `${progress}%`,
                          background:
                            "linear-gradient(90deg, #4F8CFF 0%, #0EA5E9 100%)",
                        }}
                      />
                    )}
                    {feature.title}
                  </Box>
                );
              })}
            </Box>

            {/* Descripción Activa Móvil */}
            <Box
              sx={{
                minHeight: "90px",
                bgcolor: "rgba(255,255,255,0.03)",
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                }}
              >
                {features[activeIndex].desc}
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ==============================================================
                            LAYOUT ESCRITORIO (SIN RECORTES)
             ============================================================== */
          <Box
            sx={{
              display: "flex",
              gap: 4,
              // Altura fija controlada para que encaje todo en una pantalla sin scroll
              height: "520px",
            }}
          >
            {/* LADO IZQUIERDO: Menú de navegación fijo */}
            <Box
              sx={{
                width: "35%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {features.map((feature, idx) => {
                const isActive = activeIndex === idx;
                return (
                  <Box
                    key={feature.id}
                    onClick={() => handleSelect(idx)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: "12px",
                      cursor: "pointer",
                      bgcolor: isActive
                        ? "rgba(255,255,255,0.05)"
                        : "transparent",
                      transition: "all 0.3s ease",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      "&:hover": {
                        bgcolor: isActive
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(255,255,255,0.02)",
                      },
                    }}
                  >
                    {isActive && (
                      <MotionBox
                        layoutId="activeTabIndicator"
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: "15%",
                          bottom: "15%",
                          width: "4px",
                          borderRadius: "4px",
                          background:
                            "linear-gradient(180deg, #0EA5E9 0%, #4F8CFF 100%)",
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: isActive ? 750 : 600,
                        fontSize: "1rem",
                        color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.5)",
                        pl: isActive ? 1.5 : 0,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* LADO DERECHO: Panel de Información e Imagen */}
            <Box
              sx={{
                width: "65%",
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Título y descripción fijos en la parte superior derecha */}
              <Box sx={{ mb: 3, minHeight: "90px" }}>
                <AnimatePresence mode="wait">
                  <MotionBox
                    key={activeIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.4rem",
                        color: "#FFFFFF",
                        mb: 1,
                      }}
                    >
                      {features[activeIndex].title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "1rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {features[activeIndex].desc}
                    </Typography>
                  </MotionBox>
                </AnimatePresence>
              </Box>

              {/* Contenedor de la Imagen */}
              <Box
                sx={{
                  flex: 1,
                  borderRadius: "16px",
                  bgcolor: "#0B1526", // Fondo oscuro interior
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Barra estilo macOS oscura */}
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.03)",
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#EF4444",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#F59E0B",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#10B981",
                    }}
                  />

                  {/* Línea de progreso de reproducción integrada en la barra superior */}
                  <Box
                    sx={{
                      flex: 1,
                      ml: 3,
                      mr: 1,
                      height: "2px",
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${progress}%`,
                        height: "100%",
                        background:
                          "linear-gradient(90deg, #4F8CFF 0%, #0EA5E9 100%)",
                      }}
                    />
                  </Box>
                </Box>

                {/* Imagen del Sistema (Sin recortes) */}
                <Box sx={{ position: "relative", flex: 1, p: 1 }}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key={activeIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={features[activeIndex].image}
                        alt={features[activeIndex].title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain", // Protege la imagen de recortes
                          objectPosition: "center",
                        }}
                      />
                    </MotionBox>
                  </AnimatePresence>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
